import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import PageHero from "@/components/PageHero";
import CTABand from "@/components/CTABand";
import { FadeIn, Chapter } from "@/components/Motion";
import { PRODUCTS } from "@/data/content";

const COMPARE = [
  { label: "Drive system", values: ["CDRIVE™ brushless", "CDRIVE™ brushless", "CDRIVE™ brushless"] },
  { label: "Noise level", values: ["38 dB", "38 dB", "38 dB"] },
  { label: "Staircase type", values: ["Straight", "Curved / spiral", "Outdoor curved"] },
  { label: "Rail", values: ["60 mm slim rail", "Custom twin rail", "WSAFE™ twin rail"] },
  { label: "Max user weight", values: ["160 kg (opt. 200)", "160 kg", "160 kg"] },
  { label: "Environment", values: ["Indoor", "Indoor", "Outdoor · −15°C to +45°C"] },
  { label: "Lead time", values: ["From 5 days", "15 working days", "15 working days"] },
  { label: "OEM ready", values: ["Yes", "Yes", "Yes"] },
];

export default function Products() {
  return (
    <>
      <PageHero
        overline="Product Range"
        lines={["Three machines.", "Every staircase."]}
        sub="A complete stairlift portfolio — straight, curved and the world's only outdoor curved platform — all powered by CDRIVE™ brushless technology."
      />

      <section className="bg-white pb-24" data-testid="products-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
          {PRODUCTS.map((p, i) => (
            <FadeIn key={p.slug} delay={0.05}>
              <Link
                to={`/products/${p.slug}`}
                data-testid={`product-row-${p.slug}`}
                className={`group grid grid-cols-1 md:grid-cols-2 rounded-2xl overflow-hidden border border-slate-200/80 bg-[#F8F9FA] hover:border-signal/40 transition-all duration-300 ${
                  i % 2 === 1 ? "md:[&>*:first-child]:order-2" : ""
                }`}
              >
                <div className="overflow-hidden">
                  <img
                    src={p.image}
                    alt={p.name}
                    loading="lazy"
                    className="w-full h-72 md:h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="p-8 sm:p-12 flex flex-col justify-center bg-white">
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-[10px] tracking-[0.25em] text-slate-400 uppercase">{p.code}</span>
                    {p.tech.map((t) => (
                      <span key={t} className="font-mono text-[9px] tracking-[0.15em] uppercase text-signal border border-signal/30 rounded-full px-2 py-0.5">{t}</span>
                    ))}
                  </div>
                  <h2 className="mt-4 font-display text-2xl sm:text-3xl font-semibold tracking-tight text-slate-950">{p.name}</h2>
                  <p className="mt-2 text-sm text-slate-500">{p.tagline}</p>
                  <p className="mt-4 text-sm sm:text-base text-slate-600 leading-relaxed">{p.description}</p>
                  <ul className="mt-6 space-y-2">
                    {p.highlights.slice(0, 3).map((h) => (
                      <li key={h} className="flex items-start gap-2.5 text-sm text-slate-700">
                        <span className="mt-[7px] w-1 h-1 bg-signal rounded-full shrink-0" />
                        {h}
                      </li>
                    ))}
                  </ul>
                  <span className="mt-7 inline-flex items-center gap-2 text-sm font-medium text-slate-950 group-hover:text-signal transition-colors">
                    View product <ArrowUpRight size={15} />
                  </span>
                </div>
              </Link>
            </FadeIn>
          ))}
        </div>
      </section>

      <section className="bg-[#F8F9FA] border-y border-slate-200/70 py-24" data-testid="compare-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Chapter index="02" label="Compare" />
          <h2 className="font-display text-2xl sm:text-4xl font-medium tracking-tight text-slate-950">Side by side</h2>
          <FadeIn delay={0.1}>
            <div className="mt-10 overflow-x-auto rounded-2xl border border-slate-200/80 bg-white">
              <table className="w-full min-w-[720px] text-left" data-testid="compare-table">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="p-5 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">Specification</th>
                    {PRODUCTS.map((p) => (
                      <th key={p.slug} className="p-5">
                        <span className="font-display text-base font-semibold text-slate-950">{p.name}</span>
                        <span className="block font-mono text-[9px] uppercase tracking-[0.2em] text-slate-400 mt-1">{p.code}</span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {COMPARE.map((row, i) => (
                    <tr key={row.label} className={i % 2 === 0 ? "bg-[#F8F9FA]/60" : ""}>
                      <td className="p-5 font-mono text-[11px] uppercase tracking-[0.15em] text-slate-500">{row.label}</td>
                      {row.values.map((v, j) => (
                        <td key={j} className="p-5 text-sm text-slate-800">{v}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </FadeIn>
        </div>
      </section>

      <CTABand
        overline="Distribution"
        lines={["One range.", "One factory.", "One partner."]}
      />
    </>
  );
}
